﻿import { useEffect, useMemo, useRef, useState } from 'react';
import { surveyAnalyticsService } from '../../../services/surveyAnalytics.service';
import type { QueryAccepted, QueryDefinition, QueryResult } from '../../../types/survey-analytics.types';

function isAccepted(value: QueryAccepted | QueryResult): value is QueryAccepted {
  return 'queryId' in value && !('rows' in value);
}

export function useAnalyticsQuery(input: { surveyId: string; versionId: string; definition: QueryDefinition | null; enabled?: boolean }) {
  const [data, setData] = useState<QueryResult | undefined>();
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const key = useMemo(() => JSON.stringify(input.definition ?? null), [input.definition]);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!input.enabled || !input.definition || !input.surveyId || !input.versionId) return;
    const controller = new AbortController();
    const seq = ++requestSeq.current;
    let timer: number | undefined;
    setLoading(true);
    setError(null);

    const apply = (result: QueryResult) => {
      if (seq === requestSeq.current) setData(result);
      if (seq === requestSeq.current) setLoading(false);
    };
    const fail = (err: unknown) => {
      if (controller.signal.aborted || seq !== requestSeq.current) return;
      setError(err instanceof Error ? err : new Error('Não foi possível calcular a pivot.'));
      setLoading(false);
    };

    surveyAnalyticsService.createQuery(input.surveyId, input.versionId, input.definition, { signal: controller.signal })
      .then(async response => {
        if (!isAccepted(response)) return apply(response);
        const poll = async () => {
          try {
            const next = await surveyAnalyticsService.getQuery(input.surveyId, input.versionId, response.queryId, { signal: controller.signal });
            if (!isAccepted(next)) return apply(next);
            timer = window.setTimeout(poll, next.pollAfterMs || 500);
          } catch (err) { fail(err); }
        };
        timer = window.setTimeout(poll, response.pollAfterMs || 500);
      })
      .catch(fail);

    return () => { controller.abort(); if (timer) window.clearTimeout(timer); };
  }, [input.surveyId, input.versionId, key, input.enabled]);

  return { data, error, isLoading: loading, isFetching: loading };
}
